// P6 (2026-11-02) — Adapter Call Explorer.
//
// Owner-only read-only table over bos_adapter_calls with filters,
// per-row detail, and a 7-day failure-rate rollup per channel.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import {
  channelDisplay,
  fmtDateTimeTz,
  parseDatetimeLocalInTz,
  useOrgTimezone,
} from "../lib/tz";

const CHANNELS = ["", "facebook", "instagram", "pinterest", "tiktok", "youtube", "email"];
const STATUSES = ["", "ok", "error"];

export default function AdapterCallExplorer() {
  const tz = useOrgTimezone();
  const [filters, setFilters] = useState({
    channel: "", status: "", receipt_id: "", error_code: "",
    from: "", to: "",
  });
  const [rows, setRows] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [sel, setSel] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (!v) return;
      if (k === "from" || k === "to") {
        const parsed = parseDatetimeLocalInTz(v, tz);
        if (parsed) q.set(k, parsed.toISOString());
      } else {
        q.set(k, v);
      }
    });
    return q.toString();
  }, [filters, tz]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/publish/adapter-calls?${query}`),
        api.get("/publish/adapter-calls/failure-rate?days=7"),
      ]);
      setRows(r1.data?.rows || []);
      setRollup(r2.data || null);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  async function openRow(id) {
    setSel({ loading: true });
    try {
      const r = await api.get(`/publish/adapter-calls/${id}`);
      setSel(r.data || null);
    } catch (e) {
      setSel({ error: formatApiError(e) });
    }
  }

  return (
    <Shell title="Adapter Call Explorer">
      <div className="bo-page" data-testid="adapter-call-explorer"
           style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div>
            <div className="bo-eyebrow">Publish Observability</div>
            <h1 className="bo-serif" style={{ fontSize: 26, margin: 0 }}>
              Adapter Call Explorer
            </h1>
            <p style={{ color: "var(--bo-muted)", maxWidth: 640, marginTop: 4 }}>
              Every outbound call to a social platform. 90-day retention,
              fully redacted. Owner-only. Times shown in <strong>{tz.timezone}{tz.abbr ? ` (${tz.abbr})` : ""}</strong>.
            </p>
          </div>
          <Link to="/brand-os/publish/queue" className="bo-btn bo-btn-ghost"
                style={{ textDecoration: "none" }}>
            ← Queue
          </Link>
        </div>

        {rollup && (
          <div className="bo-card" style={{ padding: 14, marginBottom: 14 }}
               data-testid="failure-rate-card">
            <div className="bo-eyebrow" style={{ marginBottom: 6 }}>
              7-day failure rate
            </div>
            <div style={{ fontSize: 15 }}>
              Overall: <strong>{rollup.overall.rate_pct}%</strong>
              {" "}· {rollup.overall.errors} / {rollup.overall.total} calls
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
              {Object.entries(rollup.by_channel).map(([ch, v]) => (
                <div key={ch} data-testid={`failure-rate-${ch}`}
                     style={{ padding: "6px 10px", borderRadius: 8,
                              border: "1px solid var(--bo-line)",
                              fontSize: 13 }}>
                  <strong>{channelDisplay(ch)}</strong>
                  : {v.rate_pct}% ({v.errors}/{v.total})
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bo-card" style={{ padding: 12, marginBottom: 12,
                                            display: "grid", gap: 8,
                                            gridTemplateColumns: "repeat(6, 1fr)",
                                            alignItems: "end" }}>
          <div>
            <label className="bo-label">Channel</label>
            <select className="bo-input" data-testid="filter-channel"
                    value={filters.channel}
                    onChange={(e) => setFilters({ ...filters, channel: e.target.value })}>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>{c || "— all —"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="bo-label">Status</label>
            <select className="bo-input" data-testid="filter-status"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s || "— all —"}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="bo-label">Error code</label>
            <input className="bo-input" data-testid="filter-error-code"
                   value={filters.error_code}
                   onChange={(e) => setFilters({ ...filters, error_code: e.target.value })} />
          </div>
          <div>
            <label className="bo-label">Receipt id</label>
            <input className="bo-input" data-testid="filter-receipt-id"
                   value={filters.receipt_id}
                   onChange={(e) => setFilters({ ...filters, receipt_id: e.target.value })} />
          </div>
          <div>
            <label className="bo-label">From</label>
            <input type="datetime-local" className="bo-input" data-testid="filter-from"
                   value={filters.from}
                   onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
          </div>
          <div>
            <label className="bo-label">To</label>
            <input type="datetime-local" className="bo-input" data-testid="filter-to"
                   value={filters.to}
                   onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
          </div>
        </div>

        {err && (
          <div className="bo-card" style={{ padding: 12, color: "var(--bo-danger)" }}
               data-testid="adapter-explorer-error">
            {typeof err === "string" ? err : err.message || JSON.stringify(err)}
          </div>
        )}

        <div className="bo-card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
                 data-testid="adapter-calls-table">
            <thead style={{ background: "var(--bo-panel-alt, #faf8f2)" }}>
              <tr>
                <th style={{ padding: 10, textAlign: "left" }}>When</th>
                <th style={{ padding: 10, textAlign: "left" }}>Channel</th>
                <th style={{ padding: 10, textAlign: "left" }}>Method</th>
                <th style={{ padding: 10, textAlign: "left" }}>URL</th>
                <th style={{ padding: 10, textAlign: "right" }}>Status</th>
                <th style={{ padding: 10, textAlign: "right" }}>Dur</th>
                <th style={{ padding: 10, textAlign: "left" }}>Error</th>
                <th style={{ padding: 10 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ padding: 12 }}>Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 12 }}>
                  No calls match these filters.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}
                    data-testid={`adapter-call-row-${r.id}`}
                    style={{ borderTop: "1px solid var(--bo-line)",
                              background: r.error_code ? "#fff3f1" : "inherit" }}>
                  <td style={{ padding: 8, fontFamily: "var(--bo-mono, monospace)" }}>
                    {fmtDateTimeTz(r.created_at, tz)}
                  </td>
                  <td style={{ padding: 8 }}>
                    {channelDisplay(r.channel)}
                  </td>
                  <td style={{ padding: 8 }}>{r.method}</td>
                  <td style={{ padding: 8, maxWidth: 320, overflow: "hidden",
                               textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={r.url}>
                    {r.url}
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>
                    {r.response_status ?? "—"}
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>
                    {r.duration_ms}ms
                  </td>
                  <td style={{ padding: 8, color: "var(--bo-danger)" }}>
                    {r.error_code || ""}
                  </td>
                  <td style={{ padding: 8 }}>
                    <button className="bo-btn bo-btn-ghost"
                            data-testid={`adapter-call-open-${r.id}`}
                            onClick={() => openRow(r.id)}
                            style={{ padding: "2px 8px", fontSize: 11 }}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {sel && (
          <div className="bo-card" style={{ padding: 14, marginTop: 14 }}
               data-testid="adapter-call-detail">
            <div style={{ display: "flex", justifyContent: "space-between",
                           alignItems: "center", marginBottom: 10 }}>
              <strong>Call detail</strong>
              <button className="bo-btn bo-btn-ghost"
                      data-testid="adapter-call-close"
                      onClick={() => setSel(null)}
                      style={{ fontSize: 12 }}>Close</button>
            </div>
            {sel.loading && <p>Loading…</p>}
            {sel.error && (
              <p style={{ color: "var(--bo-danger)" }}>
                {typeof sel.error === "string" ? sel.error : JSON.stringify(sel.error)}
              </p>
            )}
            {sel.call && (
              <div style={{ display: "grid", gap: 10 }}>
                <div>
                  <strong>{channelDisplay(sel.call.channel)}</strong> {sel.call.method}
                  {" "}<code>{sel.call.url}</code>
                  <br/>status: {sel.call.response_status} · duration:{" "}
                  {sel.call.duration_ms}ms
                </div>
                <div>
                  <div className="bo-eyebrow">Request (redacted)</div>
                  <pre style={{ background: "var(--bo-panel-alt)", padding: 10,
                                  overflowX: "auto", fontSize: 11 }}>
                    {JSON.stringify(sel.call.request_body, null, 2)}
                  </pre>
                </div>
                <div>
                  <div className="bo-eyebrow">Response excerpt</div>
                  <pre style={{ background: "var(--bo-panel-alt)", padding: 10,
                                  overflowX: "auto", fontSize: 11 }}>
                    {sel.call.response_excerpt}
                  </pre>
                </div>
                {sel.receipt && (
                  <div>
                    <div className="bo-eyebrow">Linked receipt</div>
                    <Link to={`/brand-os/publish/receipts/${sel.receipt.id}`}
                          data-testid="adapter-call-receipt-link">
                      {sel.receipt.id} · {sel.receipt.execution_status}
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}
